import { t } from "i18next";
import _ from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../hooks/useFetch";
import { useAppStore } from "../../stores/RootStore";
import { Input } from "../../ui/Input/Input";
import { ScrollArea } from "../../ui/ScrollArea/ScrollArea";
import { Label } from "../../ui/Label/Label";
import { SelectButton } from "../../ui/SelectButton/SelectButton";
import { Button } from "../../ui/Button";
import { Equipment } from "../../types/types";
import { useNavigate } from "react-router-dom";

type SelectOption = {
  value: string;
  label: string;
};

type SelectOptions = SelectOption[] | [] | undefined;

const EquipmentAdd = () => {
  const navigate = useNavigate()
  const { equipments, producers, equipmentModels, setEquipments } = useAppStore();
  const producersList: SelectOptions = producers?.map((item) => ({ label: item.attributes.name, value: item.id }));
  const [modelsList, setModelsList] = useState<SelectOptions>([]);

  const { setValue, reset, handleSubmit, control, getValues } = useForm({
    defaultValues: {
      name: "",
      factory_number: "",
      inventory_number: "",
      producer_id: null,
      equipment_model_id: null,
      qrcode: "",
    },
  });

  const [{ response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError }, doFetchItemSave] =
    useFetch(`/directories/equipment_items`);

  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "POST",
      data: {
        data: formData,
      },
    });
  };

  const addEquipmentItem = (itemSaveResponse: { data: unknown }) => {
    const newEquipmentData = itemSaveResponse.data as unknown as Equipment;
    setEquipments([...equipments!, newEquipmentData]);
  };

  useEffect(() => {
    if (!itemSaveResponse) return;
    addEquipmentItem(itemSaveResponse);
    reset();
    navigate("..", { relative: "path" });
  }, [itemSaveResponse]);

  function handleChangeProducer(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);
    setValue("equipment_model_id", null);
    setModelsList(
      equipmentModels
        ?.filter((model) => model.attributes.producer_id === Number(value?.value))
        .map((i) => ({ label: i.attributes.name, value: String(i.id) }))
    );
  }

  return (
    <ScrollArea className="w-full">
      <div className="flex flex-col max-w-[900px] w-full justify-start mx-auto pl-4 pr-16 relative overflow-hidden my-[4rem]">
        <div className="text-xl font-semibold">{t("adding_equipment")}</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-full pt-8 h-full gap-3">
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={`${t("equipment_name")}*`} />
                </div>
              )}
            />
            <Controller
              name="producer_id"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("equipment_producer")}*</Label>
                  <SelectButton
                    label={t("choose_producer")}
                    searchable={true}
                    options={producersList!}
                    value={value ? producersList?.find((s) => s.value == String(value))! : null}
                    onChange={(e) => handleChangeProducer(e, onChange)}
                  />
                </div>
              )}
            />
            <Controller
              name="equipment_model_id"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("equipment_model")}*</Label>
                  <SelectButton
                    label={t("choose_model")}
                    searchable={true}
                    options={modelsList!}
                    value={value ? modelsList?.find((s) => s.value == String(value))! : null}
                    onChange={(e) => onChange(e?.value)}
                  />
                </div>
              )}
            />
            <Controller
              name="inventory_number"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Label>{t("equipment_inventory_number")}*</Label>
                  <Input {...field} />
                </div>
              )}
            />
            <Controller
              name="factory_number"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Label>{t("equipment_factory_number")}*</Label>
                  <Input {...field} />
                </div>
              )}
            />
            <Controller
              name="qrcode"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Label>{t("qrcode")}</Label>
                  <Input {...field} />
                </div>
              )}
            />

            <Button type="submit" className="mt-8">{t("save")}</Button>
          </div>
        </form>
      </div>
    </ScrollArea>
  );
};

export default EquipmentAdd;
